/** Tiny class-flavored placeholders for dense crowds. Full KayKit loads on spotlight. */

import * as THREE from 'three'

import type { AvatarAppearance } from '../model/types'
import type { BuiltAvatar } from './buildAvatarMesh'

const geo = {
  body: new THREE.CapsuleGeometry(0.12, 0.22, 1, 5),
  bodyWide: new THREE.CapsuleGeometry(0.16, 0.2, 1, 5),
  bodyThin: new THREE.CapsuleGeometry(0.08, 0.24, 1, 5),
  head: new THREE.SphereGeometry(0.14, 5, 4),
  headSkull: new THREE.SphereGeometry(0.13, 5, 4),
  limb: new THREE.CapsuleGeometry(0.04, 0.1, 1, 4),
  hat: new THREE.ConeGeometry(0.12, 0.22, 5),
  hood: new THREE.SphereGeometry(0.16, 5, 4),
  helm: new THREE.SphereGeometry(0.15, 5, 4),
  cape: new THREE.PlaneGeometry(0.22, 0.28),
  quiver: new THREE.CapsuleGeometry(0.035, 0.16, 1, 4),
}

const matCache = new Map<string, THREE.MeshLambertMaterial>()

function mat(color: string): THREE.MeshLambertMaterial {
  let m = matCache.get(color)
  if (!m) {
    m = new THREE.MeshLambertMaterial({ color })
    matCache.set(color, m)
  }
  return m
}

type ClassLook = {
  skin: string
  top: string
  bottom: string
  accent: string
  body: 'normal' | 'wide' | 'thin'
  prop: 'none' | 'hat' | 'hood' | 'helm' | 'cape' | 'quiver' | 'bear'
  scale: number
}

function classLook(characterId: string | undefined): ClassLook {
  const id = characterId ?? ''
  if (id.includes('Skeleton_Warrior')) {
    return {
      skin: '#e8e0d0',
      top: '#6b7280',
      bottom: '#4b5563',
      accent: '#9ca3af',
      body: 'thin',
      prop: 'helm',
      scale: 0.55,
    }
  }
  if (id.includes('Skeleton_Rogue')) {
    return {
      skin: '#e8e0d0',
      top: '#374151',
      bottom: '#1f2937',
      accent: '#6b7280',
      body: 'thin',
      prop: 'hood',
      scale: 0.52,
    }
  }
  if (id.includes('Skeleton_Mage')) {
    return {
      skin: '#e8e0d0',
      top: '#5b21b6',
      bottom: '#4c1d95',
      accent: '#a78bfa',
      body: 'thin',
      prop: 'hat',
      scale: 0.52,
    }
  }
  if (id.includes('Skeleton_Minion') || id.includes('Skeleton')) {
    return {
      skin: '#e8e0d0',
      top: '#78716c',
      bottom: '#57534e',
      accent: '#a8a29e',
      body: 'thin',
      prop: 'cape',
      scale: 0.48,
    }
  }
  if (id.includes('Barbarian')) {
    return {
      skin: '#c68642',
      top: '#7c2d12',
      bottom: '#44403c',
      accent: '#a16207',
      body: 'wide',
      prop: 'bear',
      scale: 0.58,
    }
  }
  if (id.includes('Knight')) {
    return {
      skin: '#e0ac69',
      top: '#64748b',
      bottom: '#334155',
      accent: '#94a3b8',
      body: 'wide',
      prop: 'helm',
      scale: 0.56,
    }
  }
  if (id.includes('Mage')) {
    return {
      skin: '#f5c6a5',
      top: '#6d28d9',
      bottom: '#4c1d95',
      accent: '#c4b5fd',
      body: 'normal',
      prop: 'hat',
      scale: 0.54,
    }
  }
  if (id.includes('Ranger')) {
    return {
      skin: '#e0ac69',
      top: '#166534',
      bottom: '#3f3f46',
      accent: '#4d7c0f',
      body: 'normal',
      prop: 'quiver',
      scale: 0.54,
    }
  }
  if (id.includes('Rogue_Hooded') || id.includes('RogueHooded')) {
    return {
      skin: '#e0ac69',
      top: '#1e293b',
      bottom: '#0f172a',
      accent: '#334155',
      body: 'normal',
      prop: 'hood',
      scale: 0.52,
    }
  }
  if (id.includes('Rogue')) {
    return {
      skin: '#e0ac69',
      top: '#334155',
      bottom: '#1e293b',
      accent: '#475569',
      body: 'normal',
      prop: 'cape',
      scale: 0.52,
    }
  }
  return {
    skin: '#e0ac69',
    top: '#4ecdc4',
    bottom: '#2d3436',
    accent: '#74b9ff',
    body: 'normal',
    prop: 'none',
    scale: 0.52,
  }
}

function createNameSprite(text: string): THREE.Sprite {
  const canvas = document.createElement('canvas')
  canvas.width = 128
  canvas.height = 32
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('2d context unavailable')
  const draw = (label: string) => {
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    ctx.fillStyle = 'rgba(0,0,0,0.55)'
    ctx.beginPath()
    ctx.roundRect(4, 4, 120, 24, 8)
    ctx.fill()
    ctx.fillStyle = '#fff'
    ctx.font = 'bold 14px system-ui,sans-serif'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText(label.slice(0, 12), 64, 16, 110)
  }
  draw(text)
  const tex = new THREE.CanvasTexture(canvas)
  tex.colorSpace = THREE.SRGBColorSpace
  const sprite = new THREE.Sprite(
    new THREE.SpriteMaterial({ map: tex, transparent: true, depthTest: false }),
  )
  sprite.scale.set(0.45, 0.12, 1)
  sprite.position.set(0, 1.05, 0.05)
  sprite.visible = false
  ;(sprite.userData as { redraw: (t: string) => void }).redraw = (t: string) => {
    draw(t)
    tex.needsUpdate = true
  }
  return sprite
}

/**
 * Cheap class silhouette for the walking crowd.
 * Shared geos/mats — on spotlight CrowdEngine swaps to full KayKit.
 */
export function buildLiteCrowdAvatar(
  appearance: AvatarAppearance,
  displayName: string,
): BuiltAvatar {
  const look = classLook(appearance.characterId)
  // Prefer configured clothing colors when present, keep class accent/prop
  const skin = appearance.skinTone || look.skin
  const top = appearance.clothes?.topColor || look.top
  const bottom = appearance.clothes?.bottomColor || look.bottom

  const root = new THREE.Group()
  const body = new THREE.Group()
  root.add(body)

  const bodyGeo =
    look.body === 'wide' ? geo.bodyWide : look.body === 'thin' ? geo.bodyThin : geo.body

  const torso = new THREE.Mesh(bodyGeo, mat(top))
  torso.position.y = 0.48
  body.add(torso)

  const hips = new THREE.Mesh(bodyGeo, mat(bottom))
  hips.scale.set(0.8, 0.4, 0.8)
  hips.position.y = 0.28
  body.add(hips)

  const head = new THREE.Group()
  head.position.y = look.body === 'thin' ? 0.82 : 0.86
  body.add(head)
  const headMesh = new THREE.Mesh(
    look.body === 'thin' ? geo.headSkull : geo.head,
    mat(skin),
  )
  head.add(headMesh)

  if (look.prop === 'hat') {
    const hat = new THREE.Mesh(geo.hat, mat(look.accent))
    hat.position.y = 0.2
    head.add(hat)
  } else if (look.prop === 'hood') {
    const hood = new THREE.Mesh(geo.hood, mat(look.accent))
    hood.scale.set(1.15, 0.85, 1.1)
    hood.position.set(0, 0.06, -0.02)
    head.add(hood)
  } else if (look.prop === 'helm') {
    const helm = new THREE.Mesh(geo.helm, mat(look.accent))
    helm.scale.set(1.12, 0.75, 1.12)
    helm.position.y = 0.08
    head.add(helm)
  } else if (look.prop === 'bear') {
    const fur = new THREE.Mesh(geo.hood, mat(look.accent))
    fur.scale.set(1.25, 0.7, 1.2)
    fur.position.set(0, 0.1, -0.04)
    head.add(fur)
  }

  if (look.prop === 'cape' || look.prop === 'hood') {
    const cape = new THREE.Mesh(geo.cape, mat(look.accent))
    cape.position.set(0, 0.55, -0.12)
    cape.rotation.x = 0.15
    body.add(cape)
  }
  if (look.prop === 'quiver') {
    const q = new THREE.Mesh(geo.quiver, mat(look.accent))
    q.position.set(0.14, 0.55, -0.08)
    q.rotation.z = 0.35
    body.add(q)
  }

  const leftArm = new THREE.Group()
  leftArm.position.set(look.body === 'wide' ? -0.24 : -0.18, 0.62, 0)
  body.add(leftArm)
  const la = new THREE.Mesh(geo.limb, mat(top))
  la.position.y = -0.08
  leftArm.add(la)

  const rightArm = new THREE.Group()
  rightArm.position.set(look.body === 'wide' ? 0.24 : 0.18, 0.62, 0)
  body.add(rightArm)
  const ra = new THREE.Mesh(geo.limb, mat(top))
  ra.position.y = -0.08
  rightArm.add(ra)

  const leftLeg = new THREE.Mesh(geo.limb, mat(bottom))
  leftLeg.position.set(-0.08, 0.12, 0)
  body.add(leftLeg)

  const rightLeg = new THREE.Mesh(geo.limb, mat(bottom))
  rightLeg.position.set(0.08, 0.12, 0)
  body.add(rightLeg)

  body.userData.groundY = 0
  body.scale.setScalar(look.scale)

  const nameSprite = createNameSprite(displayName)
  nameSprite.position.y = 0.95
  root.add(nameSprite)
  root.rotation.y = Math.PI / 2

  return {
    root,
    body,
    leftArm,
    rightArm,
    leftLeg,
    rightLeg,
    head,
    nameSprite,
    quality: 'low',
    setNameVisible: (v) => {
      nameSprite.visible = v
    },
    updateName: (name) => {
      ;(nameSprite.userData as { redraw: (t: string) => void }).redraw(name)
    },
    dispose: () => {
      const map = (nameSprite.material as THREE.SpriteMaterial).map
      map?.dispose()
      ;(nameSprite.material as THREE.Material).dispose()
    },
  }
}
